import { Assert } from '@team/assert';
import { Cache } from '@team/cache';
import { ArrayKit } from '@team/array';
import { LogLevel } from './enums';
import type { Executor } from './types';
import type { LogEntry, Logger } from './interfaces';

export class ConsoleLogger implements Logger {
  private readonly logsCache = new Cache<boolean, unknown[]>();
  private lastLogEntry: Nullable<LogEntry> = null;
  #deferredLogs: LogEntry[] = [];

  #isEnabled = import.meta.env.DEV;

  constructor(private readonly scope: string) {}

  public enable(): this {
    this.#isEnabled = true;
    this.flushDeferredLogs();

    return this;
  }

  public disable(): this {
    this.#isEnabled = false;

    return this;
  }

  private flushDeferredLogs() {
    if (!this.#isEnabled) {
      return;
    }

    if (ArrayKit.isEmpty(this.#deferredLogs)) {
      return;
    }

    this.#deferredLogs.forEach((logEntry) => {
      this.printLogEntry(logEntry);
    });

    this.#deferredLogs = [];
  }

  private handle(level: LogLevel, args: unknown[]) {
    const logEntry = this.createLogEntry(level, args);

    if (this.isLogEntryCached(logEntry)) {
      return;
    }

    if (!this.#isEnabled) {
      this.#deferredLogs.push(logEntry);

      return;
    }

    this.printLogEntry(logEntry);

    this.lastLogEntry = logEntry;
  }

  public once(): this {
    if (Assert.isNull(this.lastLogEntry)) {
      this.warn('Tried to cache but no log found');

      return this;
    }

    if (this.isLogEntryCached(this.lastLogEntry)) {
      return this;
    }

    const isEmptyArgs = ArrayKit.isEmpty(this.lastLogEntry.args);

    if (isEmptyArgs) {
      this.warn('Tried to cache but no args provided for log');

      return this;
    }

    this.cacheLogEntry(this.lastLogEntry);

    return this;
  }

  private printLogEntry({ level, args }: LogEntry) {
    const print = ConsoleLogger.getExecutorByLogLevel(level);

    print(...this.getFormatterScope(), ...args);
  }

  private createLogEntry(level: LogLevel, args: unknown[]): LogEntry {
    return {
      level,
      args,
      timestamp: Date.now(),
    };
  }

  private isLogEntryCached({ args: key }: LogEntry) {
    return this.logsCache.has(key);
  }

  private cacheLogEntry({ args }: LogEntry) {
    this.logsCache.set(args, true);
  }

  private getFormatterScope(): [scope: string, styles: string] {
    return [`%c[${this.scope}]`, 'font-weight: bold'];
  }

  private static getExecutorByLogLevel(level: LogLevel) {
    return ConsoleLogger.executorMap[level];
  }

  public log(...args: NotEmptyArray<unknown>): this {
    this.handle(LogLevel.LOG, args);

    return this;
  }

  public info(...args: NotEmptyArray<unknown>): this {
    this.handle(LogLevel.INFO, args);

    return this;
  }

  public warn(...args: NotEmptyArray<unknown>): this {
    this.handle(LogLevel.WARN, args);

    return this;
  }

  public error(...args: NotEmptyArray<unknown>): this {
    this.handle(LogLevel.ERROR, args);

    return this;
  }

  private static readonly executorMap: Record<LogLevel, Executor> = {
    [LogLevel.LOG]: console.log,
    [LogLevel.INFO]: console.info,
    [LogLevel.WARN]: console.warn,
    [LogLevel.ERROR]: console.error,
  };
}
